/**
 * Тарифы: выбор периода и того, куда зачислить срок.
 *
 * Подписок у человека может быть несколько, поэтому покупка всегда отвечает
 * на вопрос «куда»: завести новую подписку (ещё три устройства) или продлить
 * одну из действующих. Без этого шага оплата не знала бы, что делать с
 * деньгами, — а угадывать за человека нельзя.
 *
 * Оплата — звёздами Telegram: окно оплаты открывается поверх приложения,
 * а после зачисления показывается модалка с итогом.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { usePlans, useSubscription } from '@/entities/subscription/queries';
import { PaymentSuccess } from '@/features/payment/PaymentSuccess';
import { TelegramPrompt } from '@/features/guest/TelegramPrompt';
import { usePayStars, type PayPhase } from '@/features/payment/usePayStars';
import { isGuest } from '@/shared/lib/guest';
import type { Period, Plan, SubscriptionItem } from '@/shared/api/schemas';
import { daysWord, formatPeriod, formatShortDate } from '@/shared/lib/format';
import { Notice, Screen } from '@/shared/ui/Screen';
import { Button } from '@/shared/ui/kit/Button';
import { GroupCaption } from '@/shared/ui/kit/Card';
import { Icon, type IconName } from '@/shared/ui/kit/Icon';
import { Sheet } from '@/shared/ui/kit/Sheet';

import styles from './PlansPage.module.css';

/** Валюта показа. Звёзды появятся вместе с оплатой в Telegram. */
const DISPLAY_CURRENCY = 'RUB';

const NEW = 'new';

/**
 * Способы оплаты.
 *
 * У каждого своя валюта и своя цена: рубли для СБП и карты, звёзды для
 * Telegram Stars. Курс не применяется — цены ведутся отдельно
 * (docs/architecture/12-billing.md §9).
 *
 * СБП и карта появятся с подключением платёжного провайдера; до тех пор
 * они видны, но недоступны — так человек знает, что они будут.
 */
type MethodId = 'sbp' | 'card' | 'stars';

interface Method {
  id: MethodId;
  title: string;
  icon: IconName;
  currency: string;
  available: boolean;
}

const METHODS: readonly Method[] = [
  { id: 'sbp', title: 'СБП', icon: 'sbp', currency: 'RUB', available: false },
  { id: 'card', title: 'Карта', icon: 'card', currency: 'RUB', available: false },
  { id: 'stars', title: 'Stars', icon: 'star', currency: 'XTR', available: true },
];

function priceIn(period: Period, currency: string) {
  return period.prices.find((p) => p.currency === currency);
}

/** Способ можно выбрать: он подключён и у периода есть цена в его валюте. */
function usable(method: Method, period: Period): boolean {
  return method.available && priceIn(period, method.currency) !== undefined;
}

const PAY_LABEL: Record<Exclude<PayPhase, 'idle'>, string> = {
  creating: 'Выставляем счёт…',
  paying: 'Ожидаем оплату…',
  confirming: 'Зачисляем срок…',
};

const rub = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

function priceOf(period: Period) {
  return period.prices.find((p) => p.currency === DISPLAY_CURRENCY) ?? period.prices[0];
}

/** Месяцев в периоде: 30 дней — месяц, год — двенадцать, а не 12,17. */
function monthsOf(period: Period): number | null {
  if (period.duration_days === null) return null;
  return period.duration_days >= 360 ? 12 : Math.round(period.duration_days / 30);
}

/**
 * Цена в месяц и выгода против помесячной оплаты.
 *
 * Считается только в рублях: у звёзд другая шкала цен, и проценты
 * между валютами смысла не имеют.
 */
function perMonth(period: Period, base: Period | undefined) {
  const price = priceOf(period);
  const months = monthsOf(period);
  if (!price || price.currency !== DISPLAY_CURRENCY || !months || months <= 1) return null;

  const monthly = price.amount_minor / 100 / months;
  const basePrice = base ? priceOf(base) : undefined;
  if (!basePrice || basePrice.currency !== DISPLAY_CURRENCY) {
    return { monthly: `${rub.format(monthly)} ₽ / мес`, save: 0, full: null };
  }
  const baseMonthly = basePrice.amount_minor / 100;
  const save = Math.round((1 - monthly / baseMonthly) * 100);
  return {
    monthly: `${rub.format(monthly)} ₽ / мес`,
    save: save >= 5 ? save : 0,
    // Сколько стоили бы те же месяцы при помесячной оплате — зачёркнутая
    // цена рядом с настоящей показывает выгоду нагляднее процента.
    full: save >= 5 ? `${rub.format(baseMonthly * months)} ₽` : null,
  };
}

function targetLabel(item: SubscriptionItem, number: number): { title: string; hint: string } {
  const left =
    item.days_left !== null && item.days_left > 0
      ? ` · осталось ${item.days_left} ${daysWord(item.days_left)}`
      : '';
  return {
    title: `Продлить подписку ${number}`,
    hint: item.expires_at ? `до ${formatShortDate(item.expires_at)}${left}` : 'бессрочная',
  };
}

function Choice({
  selected,
  title,
  hint,
  onSelect,
}: {
  selected: boolean;
  title: string;
  hint: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={selected ? `${styles.option} ${styles.selected}` : styles.option}
      onClick={onSelect}
    >
      <span className={styles.radio}>{selected ? <Icon name="check" size={14} /> : null}</span>
      <span className={styles.body}>
        <span className={styles.name}>{title}</span>
        <span className={styles.monthly}>{hint}</span>
      </span>
    </button>
  );
}

export function PlansPage() {
  const plans = usePlans();
  const subscription = useSubscription();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const payment = usePayStars();
  const guest = isGuest();
  const [prompt, setPrompt] = useState(false);

  const [chosen, setChosen] = useState<{ plan: Plan; period: Period } | null>(null);
  // Куда зачислить: id подписки или «новая». Если пришли с экрана подписки
  // по «Продлить», она уже выбрана.
  const [target, setTarget] = useState<string>(params.get('extend') ?? NEW);
  const [methodId, setMethodId] = useState<MethodId>('stars');

  const accessible = (subscription.data?.items ?? []).filter((item) => item.has_access);

  const open = (plan: Plan, period: Period) => {
    // Без явного выбора — продление ближайшей к окончанию: это самый частый
    // повод открыть тарифы. Новая подписка — осознанное «нужно больше устройств».
    const preset = params.get('extend');
    setTarget(preset ?? accessible[0]?.id ?? NEW);
    payment.reset();
    // Первый доступный по порядку: порядок в METHODS — это и приоритет.
    setMethodId(METHODS.find((m) => usable(m, period))?.id ?? 'stars');
    setChosen({ plan, period });
  };

  const paidId = payment.result?.payment?.subscription_id ?? null;
  const paidIndex = paidId ? (subscription.data?.items.findIndex((i) => i.id === paidId) ?? -1) : -1;

  const method = METHODS.find((m) => m.id === methodId) ?? METHODS[0];
  const methodPrice = chosen && method ? priceIn(chosen.period, method.currency) : undefined;
  const canPay = Boolean(chosen && method && usable(method, chosen.period));

  const startPayment = () => {
    // В браузере оплатить нельзя: покупка привязана к аккаунту Telegram.
    if (guest) {
      setPrompt(true);
      return;
    }
    // Пока подключены только звёзды; СБП и карта пойдут через провайдера.
    if (!chosen || method?.id !== 'stars') return;
    void payment
      .pay({
        plan_code: chosen.plan.code,
        period_code: chosen.period.code,
        extend_subscription_id: target === NEW ? null : target,
      })
      .then((paid) => {
        if (paid) setChosen(null);
      });
  };

  if (!plans.isLoading && !plans.error && plans.data?.length === 0) {
    return <Notice header="Тарифов пока нет" description="Загляните чуть позже." />;
  }

  return (
    <Screen isLoading={plans.isLoading} error={plans.error} onRetry={() => void plans.refetch()}>
      <h1 className={styles.head}>Тарифы</h1>

      {plans.data?.map((plan) => {
        const base = plan.periods.find((p) => monthsOf(p) === 1);
        // Самый выгодный срок подсвечивается: человеку проще согласиться
        // с подсказкой, чем сравнивать четыре цены в уме.
        const best = plan.periods
          .map((period) => ({ code: period.code, save: perMonth(period, base)?.save ?? 0 }))
          .reduce((top, item) => (item.save > top.save ? item : top), { code: '', save: 0 });
        return (
          <div key={plan.code} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <GroupCaption>{plan.title}</GroupCaption>
            {plan.periods.map((period) => {
              const price = priceOf(period);
              const extra = perMonth(period, base);
              const isBest = best.save > 0 && period.code === best.code;
              return (
                <button
                  key={period.code}
                  type="button"
                  className={isBest ? `${styles.period} ${styles.best}` : styles.period}
                  onClick={() => open(plan, period)}
                >
                  {isBest ? <span className={styles.bestLabel}>Выгоднее всего</span> : null}
                  <span className={styles.body}>
                    <span className={styles.name}>
                      {formatPeriod(period.code, period.duration_days)}
                      {extra?.save ? <span className={styles.save}>−{extra.save}%</span> : null}
                    </span>
                    <span className={styles.monthly}>{extra?.monthly ?? 'помесячно'}</span>
                  </span>
                  <span className={styles.right}>
                    <span className={styles.price}>{price?.formatted ?? '—'}</span>
                    {extra?.full ? <s className={styles.full}>{extra.full}</s> : null}
                  </span>
                </button>
              );
            })}
            <p className={styles.note}>
              {plan.device_limit ? `До ${plan.device_limit} устройств на подписку` : 'Без ограничения устройств'}
              {' · трафик без ограничений'}
            </p>
          </div>
        );
      })}

      <Sheet
        open={chosen !== null}
        title={
          // Без цены: она зависит от способа оплаты и показана ниже.
          chosen ? formatPeriod(chosen.period.code, chosen.period.duration_days) : ''
        }
        onClose={() => setChosen(null)}
        fit
      >
        <p className={styles.caption}>Куда зачислить срок</p>
        <div className={styles.radiogroupEnd} role="radiogroup" aria-label="Куда зачислить срок">
          <Choice
            selected={target === NEW}
            title="Новая подписка"
            hint={`ещё ${chosen?.plan.device_limit ?? 3} устройства`}
            onSelect={() => setTarget(NEW)}
          />
          {accessible.map((item) => {
            const number = (subscription.data?.items.indexOf(item) ?? 0) + 1;
            const { title, hint } = targetLabel(item, number);
            return (
              <Choice
                key={item.id}
                selected={target === item.id}
                title={title}
                hint={hint}
                onSelect={() => setTarget(item.id)}
              />
            );
          })}
        </div>
        <p className={styles.caption}>Способ оплаты</p>
        <div className={styles.methods} role="radiogroup" aria-label="Способ оплаты">
          {METHODS.map((m) => {
            const price = chosen ? priceIn(chosen.period, m.currency) : undefined;
            const enabled = chosen ? usable(m, chosen.period) : false;
            const selected = enabled && m.id === methodId;
            return (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={!enabled}
                className={[styles.method, selected ? styles.selected : ''].filter(Boolean).join(' ')}
                onClick={() => setMethodId(m.id)}
              >
                <Icon name={m.icon} size={22} />
                <span className={styles.methodName}>{m.title}</span>
                <span className={styles.methodPrice}>
                  {!m.available ? (
                    'скоро'
                  ) : price && m.currency === 'XTR' ? (
                    <>
                      {price.amount_minor}
                      <Icon name="star" size={11} />
                    </>
                  ) : (
                    (price?.formatted ?? '—')
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <div className={styles.pay}>
          <Button
            block
            disabled={!guest && (!canPay || payment.phase !== 'idle')}
            onClick={startPayment}
          >
            {guest ? (
              'Оформить в Telegram'
            ) : payment.phase !== 'idle' ? (
              PAY_LABEL[payment.phase]
            ) : canPay && methodPrice ? (
              method?.currency === 'XTR' ? (
                <>
                  Оплатить {methodPrice.amount_minor}
                  <Icon name="star" size={17} />
                </>
              ) : (
                `Оплатить ${methodPrice.formatted}`
              )
            ) : (
              'Оплата недоступна'
            )}
          </Button>
        </div>
        {payment.error ? (
          <p className={`${styles.payNote} ${styles.payError}`} role="alert">
            {payment.error}
          </p>
        ) : (
          <p className={styles.payNote}>
            {guest ? 'Оплата и подписка — в нашем Telegram-боте' : 'Срок зачислится сразу после оплаты'}
          </p>
        )}
      </Sheet>

      <TelegramPrompt
        open={prompt}
        action="Оформить подписку"
        onClose={() => setPrompt(false)}
      />

      <PaymentSuccess
        result={payment.result}
        number={paidIndex >= 0 ? paidIndex + 1 : null}
        onClose={payment.reset}
        onOpen={(id) => {
          payment.reset();
          void navigate(id ? `/subscriptions/${id}` : '/subscriptions');
        }}
      />
    </Screen>
  );
}
